import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlatformProvider } from './contexts/PlatformContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ReferenceProvider } from './contexts/ReferenceContext';
import LoginForm from './components/Auth/LoginForm';
import DashboardLayout from './components/Dashboard/DashboardLayout';
import Overview from './components/Dashboard/Overview';
import Transactions from './components/Dashboard/TransactionHistory';
import TransactionHistoryCards from './components/Dashboard/TransactionHistoryCards';
import Analytics from './components/Dashboard/Analytics';
import AIInsights from './components/Dashboard/AIInsights';
import AIInsightCardDesigns from './components/Dashboard/AIInsightCardDesigns';
import Customers from './components/Dashboard/Customers';
import Merchants from './components/Dashboard/Merchants';
import Users from './components/Dashboard/Users';
import PaymentMethods from './components/Dashboard/PaymentMethods';
import Settings from './components/Dashboard/Settings';
import AuditLogs from './components/Dashboard/AuditLogs';
import ExcelUpload from './components/Dashboard/ExcelUpload';
import TestAnalytics from './components/TestAnalytics';
import { Toaster } from './components/ui/toaster';
import './App.css';
import './theme.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <LoginForm />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/analytics-test" element={<TestAnalytics />} />
      <Route path="/login" element={<LoginForm />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Overview />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/transactions"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Transactions />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/transactions-cards"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TransactionHistoryCards />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/analytics"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Analytics />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/ai-insights"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AIInsights />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/ai-insights-designs"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AIInsightCardDesigns />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/customers"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Customers />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/merchants"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Merchants />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/users"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Users />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/payment-methods"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PaymentMethods />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/audit-logs"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AuditLogs />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/excel-upload"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ExcelUpload />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <PlatformProvider>
          <ThemeProvider>
            <ReferenceProvider>
              <div className="App">
                <AppRoutes />
                <Toaster />
              </div>
            </ReferenceProvider>
          </ThemeProvider>
        </PlatformProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;